import { NavTag } from "@/design-system/Tags/NewTag/NewTag";
import { userSubscriptionSelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import {
  ArrowLeftRight,
  BookOpen,
  ChartNoAxesColumn,
  CircleHelp,
  ClipboardList,
  CreditCard,
  Dna,
  Download,
  FlaskConical,
  GalleryVerticalEnd,
  Gauge,
  Images,
  LayoutGrid,
  Library,
  type LucideIcon,
  Mail,
  Newspaper,
  PiggyBank,
  Puzzle,
  Search,
  Shield,
  Shirt,
  Smartphone,
  Sparkles,
  Star,
  Trophy,
  Users,
  Wand2,
} from "lucide-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";

export type SiteNavSectionKey =
  | "sbcs"
  | "gallery"
  | "squads"
  | "objectives"
  | "players"
  | "evolutions"
  | "app"
  | "club"
  | "upgrade"
  | "explainers"
  | "extra";

export type SiteNavPageKey =
  | "allSbcs"
  | "galleryPlanner"
  | "galleryLevels"
  | "objectives"
  | "squadBuilder"
  | "tactics"
  | "playerDatabase"
  | "metaRating"
  | "cheapestItemScore"
  | "playstylesLab"
  | "compare"
  | "evoOverview"
  | "evoPlayers"
  | "evoBuilder"
  | "myEvolutions"
  | "evoTracker"
  | "appLanding"
  | "clubOverview"
  | "importClub"
  | "subscription"
  | "news"
  | "essentials"
  | "contact";

type PageDef = {
  key: SiteNavPageKey;
  href: string;
  icon: LucideIcon;
  tag?: NavTag;
  /** Extra route prefixes that count as this page being current. */
  matches?: string[];
  /** Listed in the site map only, not in the mega panel. */
  siteMapOnly?: boolean;
};

type SectionDef = {
  key: SiteNavSectionKey;
  icon: LucideIcon;
  /** Listed in the site map only, not in the bar. */
  siteMapOnly?: boolean;
  /** No heading in the site map: its page stands alone at the top of a column. */
  headless?: boolean;
  pages: PageDef[];
};

/*
  One structure for every nav surface (bar, panels, site map, rail). The first
  page of a section is its main page: where the section label itself links.
*/
const SECTIONS: SectionDef[] = [
  {
    key: "sbcs",
    icon: Puzzle,
    pages: [
      {
        key: "allSbcs",
        href: "/sbc",
        icon: Puzzle,
      },
      {
        key: "cheapestItemScore",
        href: "/cheapest-item-score",
        icon: PiggyBank,
      },
    ],
  },
  {
    key: "players",
    icon: Users,
    pages: [
      { key: "playerDatabase", href: "/players", icon: Search },
      { key: "playstylesLab", href: "/playstyles-lab", icon: FlaskConical },
      { key: "compare", href: "/compare", icon: ArrowLeftRight },
    ],
  },
  {
    key: "squads",
    icon: Shirt,
    pages: [
      { key: "squadBuilder", href: "/squad-builder", icon: Wand2 },
      { key: "tactics", href: "/tactics", icon: LayoutGrid },
    ],
  },
  {
    key: "gallery",
    icon: GalleryVerticalEnd,
    pages: [
      {
        key: "galleryPlanner",
        href: "/fut-gallery",
        icon: Images,
        tag: "new",
      },
      {
        key: "galleryLevels",
        href: "/fut-gallery/levels",
        icon: Trophy,
        tag: "new",
      },
    ],
  },
  {
    key: "objectives",
    icon: ClipboardList,
    siteMapOnly: true,
    pages: [
      {
        key: "objectives",
        href: "/objectives",
        icon: ClipboardList,
        tag: "soon",
        matches: ["/objective-plan"],
      },
    ],
  },
  {
    key: "evolutions",
    icon: Dna,
    pages: [
      { key: "evoOverview", href: "/evolutions", icon: Library },
      { key: "evoPlayers", href: "/evolutions/players", icon: Star },
      { key: "evoBuilder", href: "/evolutions/builder", icon: Sparkles },
      {
        key: "myEvolutions",
        href: "/evolutions/my-evolutions/players",
        icon: ChartNoAxesColumn,
      },
    ],
  },
  {
    key: "app",
    icon: Smartphone,
    pages: [{ key: "appLanding", href: "/app", icon: Smartphone, tag: "soon" }],
  },
  {
    key: "club",
    icon: Shield,
    pages: [{ key: "clubOverview", href: "/my-club", icon: Shield }],
  },
  {
    key: "upgrade",
    icon: CreditCard,
    siteMapOnly: true,
    headless: true,
    pages: [
      {
        key: "subscription",
        href: "/subscription",
        icon: CreditCard,
        matches: ["/payment-success"],
      },
    ],
  },
  {
    key: "explainers",
    icon: CircleHelp,
    siteMapOnly: true,
    pages: [
      {
        key: "importClub",
        href: "/club-import-tutorial",
        icon: Download,
        matches: ["/import-mobile", "/import-troubleshoot"],
      },
      { key: "metaRating", href: "/meta-rating", icon: Gauge },
      { key: "evoTracker", href: "/evolutions/tracker", icon: CircleHelp },
      { key: "essentials", href: "/essentials", icon: BookOpen },
    ],
  },
  {
    key: "extra",
    icon: BookOpen,
    siteMapOnly: true,
    pages: [
      { key: "news", href: "/news", icon: Newspaper },
      { key: "contact", href: "/contact", icon: Mail },
    ],
  },
];

/** Desktop Menu columns; the last group is info, shown muted. */
export const SITE_MAP_COLUMNS: SiteNavSectionKey[][] = [
  ["sbcs", "players"],
  ["squads", "evolutions"],
  ["club", "gallery", "objectives", "app"],
  ["upgrade", "explainers", "extra"],
];

export type SiteNavPage = PageDef & {
  label: string;
  description: string;
  /** Drawn in primary blue: the Upgrade entry for free users. */
  highlight?: boolean;
};
export type SiteNavSection = Omit<SectionDef, "pages"> & {
  label: string;
  blurb: string;
  href: string;
  pages: SiteNavPage[];
};

type TitledSectionKey = Exclude<SiteNavSectionKey, "upgrade">;

export const useSiteNavSections = (): SiteNavSection[] => {
  const { t } = useTranslation(["nav", "common"]);
  const subscription = useSelector(userSubscriptionSelector);
  const isFree = !subscription || subscription === "free";
  return useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        // A headless section is never titled, so it has no strings of its own.
        label: section.headless
          ? ""
          : t(`siteNav.sections.${section.key as TitledSectionKey}.label`),
        blurb: section.headless
          ? ""
          : t(`siteNav.sections.${section.key as TitledSectionKey}.blurb`),
        href: section.pages[0].href,
        pages: section.pages.map((page) => {
          const upgrade = page.key === "subscription" && isFree;
          return {
            ...page,
            label: upgrade
              ? t("common:upgrade")
              : t(`siteNav.pages.${page.key}.label`),
            description: t(`siteNav.pages.${page.key}.description`),
            highlight: upgrade,
          };
        }),
      })),
    [t, isFree],
  );
};

const isUnder = (path: string, prefix: string) =>
  path === prefix || path.startsWith(prefix + "/");

/** The current section and page, by the longest matching route prefix. */
export const findActiveNav = (
  sections: SiteNavSection[],
  pathname: string,
): { sectionKey?: SiteNavSectionKey; pageKey?: SiteNavPageKey } => {
  let best: { sectionKey?: SiteNavSectionKey; pageKey?: SiteNavPageKey } = {};
  let bestLength = 0;
  for (const section of sections) {
    for (const page of section.pages) {
      for (const prefix of [page.href, ...(page.matches ?? [])]) {
        if (isUnder(pathname, prefix) && prefix.length > bestLength) {
          best = { sectionKey: section.key, pageKey: page.key };
          bestLength = prefix.length;
        }
      }
    }
  }
  return best;
};
